import { useState } from "react";
import { BackLink } from "../ui/BackLink";
import { useAuth } from "../auth";
import {
  MESSAGE_DEFS,
  renderTemplate,
  saveTemplate,
  templateText,
  useMessageTemplates,
  type MessageDef,
} from "../messages";

/**
 * The message centre: every WhatsApp message the Today cards offer, editable.
 *
 * "מרכז הודעות והגדרות שאני שם עורך את כל הטקסטים של כל ההודעות אם אני לא אוהב
 * את מה שאתה כתבת." The defaults are in `messages.ts`; this screen writes only
 * the ones somebody changed, so a default improved in a later release still
 * reaches every message nobody touched.
 *
 * Each message shows a live preview with sample facts, because a template with
 * `[ ל{מטרה}]` in it is hard to judge until you see it filled in.
 */

const SAMPLE: Record<string, string> = {
  שם: "ישראל",
  סכום: "180 ₪",
  מטרה: "ברכת השנה",
  נפטר: "אברהם בן שרה",
  קרבה: "אבא",
  תאריך: "ג׳ בתשרי",
  מתי: "בעוד 3 ימים",
  נושא: "שיעור חדש",
  אירוע: "סעודת ראש השנה",
  בקשה: "בדיקת מזוזות",
};

export function MessagesPage() {
  const { role, user } = useAuth();
  const canEdit = role === "owner" || role === "admin";
  const { templates, loaded } = useMessageTemplates();

  const messages = MESSAGE_DEFS.filter((d) => !d.key.startsWith("signature_"));
  const signatures = MESSAGE_DEFS.filter((d) => d.key.startsWith("signature_"));

  return (
    <div className="screen">
      <header className="hdr">
        <div className="hdr-top">
          <div className="hdr-titles">
            <h1>מרכז הודעות</h1>
            <p className="sub">הנוסח של הודעות הוואטסאפ שמופיעות בכרטיסים במסך היום</p>
          </div>
        </div>
      </header>

      <div className="body">
        <BackLink fallback="/more" label="חזרה" />

        <div className="notice info">
          <b>איך זה עובד:</b> מילה בסוגריים מסולסלים, כמו <span className="mono">{"{שם}"}</span>, מתחלפת
          בפרטים של הכרטיס. טקסט בסוגריים מרובעים, כמו <span className="mono">[ של {"{סכום}"}]</span>, יופיע
          רק אם יש בו נתון. <span className="mono">{"{חתימה}"}</span> היא החתימה של מי ששולח.
        </div>

        {!canEdit && <div className="notice info">רק מנהלים יכולים לשנות את הנוסח. אפשר לעיין.</div>}

        {!loaded ? (
          <div className="skeleton" style={{ height: 150 }} />
        ) : (
          <>
            <div className="section-title" style={{ margin: "4px 4px 8px" }}>
              חתימות
            </div>
            {signatures.map((d) => (
              <TemplateCard key={d.key} def={d} saved={templates[d.key]} canEdit={canEdit} uid={user?.uid} />
            ))}

            <div className="section-title" style={{ margin: "18px 4px 8px" }}>
              הודעות
            </div>
            {messages.map((d) => (
              <TemplateCard
                key={d.key}
                def={d}
                saved={templates[d.key]}
                canEdit={canEdit}
                uid={user?.uid}
                signature={templateText(templates, "signature_mendi")}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}

function TemplateCard({
  def,
  saved,
  canEdit,
  uid,
  signature,
}: {
  def: MessageDef;
  saved: string | undefined;
  canEdit: boolean;
  uid: string | undefined;
  signature?: string;
}) {
  const current = saved ?? def.text;
  const [text, setText] = useState(current);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const dirty = text !== current;
  const changedFromDefault = saved !== undefined;
  const preview = renderTemplate(text, { ...SAMPLE, חתימה: signature ?? "" });

  async function save(value: string | null) {
    if (!uid) return;
    setBusy(true);
    setNote(null);
    try {
      await saveTemplate(def.key, value, uid);
      if (value === null) setText(def.text);
      setNote(value === null ? "החזרנו לנוסח המקורי." : "שמרנו.");
    } catch {
      setNote("לא הצלחנו לשמור. נסו שוב.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-body">
        <p className="name-row">
          {def.title}
          {changedFromDefault && <span className="faint"> · שיניתם</span>}
        </p>
        <p className="faint">{def.when}</p>
        {def.vars.length > 0 && (
          <div className="chips" style={{ marginTop: 6 }}>
            {["שם", ...def.vars, "חתימה"].map((v) => (
              <button
                key={v}
                className="tagchip"
                disabled={!canEdit || busy}
                onClick={() => setText((t) => `${t}{${v}}`)}
                title="הוספה לסוף ההודעה"
              >
                {`{${v}}`}
              </button>
            ))}
          </div>
        )}
        <textarea
          className="form-input"
          style={{ marginTop: 8, minHeight: def.key.startsWith("signature_") ? 48 : 110 }}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={!canEdit || busy}
          dir="rtl"
        />
        {!def.key.startsWith("signature_") && (
          <>
            <p className="card-title" style={{ marginTop: 8 }}>
              ככה זה ייראה
            </p>
            <p className="muted" style={{ whiteSpace: "pre-wrap" }}>
              {preview}
            </p>
          </>
        )}
        {canEdit && (
          <div className="btn-row" style={{ marginTop: 10 }}>
            <button className="btn btn-primary" disabled={!dirty || busy} onClick={() => save(text)}>
              {busy ? "שומרים…" : "שמרו"}
            </button>
            {changedFromDefault && (
              <button className="btn" disabled={busy} onClick={() => save(null)}>
                חזרה לנוסח המקורי
              </button>
            )}
          </div>
        )}
        {note && <p className="faint" style={{ marginTop: 6 }}>{note}</p>}
      </div>
    </div>
  );
}
